import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";

const EmployeeList = () => {
  const URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const [employees, setEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("firstName");
  const [sortOrder, setSortOrder] = useState("asc");
  
  useEffect(() => {
    if (location.state?.message) {
      const toastId = location.state.toastId || "success-toast";
      if (!toast.isActive(toastId)) {
        toast.success(location.state.message, { id: toastId });
      }
    }
    const fetchEmployees = async () => {
      try {
        const params = {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm,
          sortField: sortField,
          sortOrder: sortOrder,
        };

        const response = await axios.get(`${URL}/employees`, {
          params,
          withCredentials: true,
        });

        if (response.data && response.data.data) {
          setEmployees(response.data.data);
          setTotalPages(Math.ceil(response.data.total / itemsPerPage));
        } else {
          setEmployees([]);
          setTotalPages(0);
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          toast.error("Unauthorized access. Please log in again.");
          navigate("/login");
        } else {
          toast.error("Failed to fetch employees");
        }
      }
    };

    fetchEmployees();
  }, [URL, currentPage, itemsPerPage, searchTerm, sortField, sortOrder, navigate, location.state]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDelete = (employeeId) => {
    toast((t) => (
      <span>
        คุณมั่นใจที่จะลบข้อมูลช่องทางนี้?
        <div className="mt-2 flex justify-end space-x-2">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              axios
                .delete(`${URL}/employees/${employeeId}`, {
                  withCredentials: true,
                })
                .then(() => {
                  const fetchUpdatedEmployees = async () => {
                    const response = await axios.get(`${URL}/employees`, {
                      params: {
                        page: currentPage,
                        limit: itemsPerPage,
                        search: searchTerm,
                      },
                      withCredentials: true,
                    });

                    if (response.data && response.data.data) {
                      setEmployees(response.data.data);
                      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
                    } else {
                      setEmployees([]);
                      setTotalPages(0);
                    }
                  };

                  fetchUpdatedEmployees();
                  toast.success("ลบข้อมูลเรียบร้อยแล้ว");
                })
                .catch(() => toast.error("ลบข้อมูลไม่สำเร็จ"));
            }}
            className="ml-2 text-red-500"
          >
            ยืนยัน
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="ml-2 text-gray-500"
          >
            ยกเลิก
          </button>
        </div>
      </span>
    ));
  };

  const handleAdd = () => {
    navigate("/panel/employee/add");
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">การจัดการข้อมูลพนักงาน</h2>
      <hr></hr>
      <div className="flex justify-between items-center mb-4 mt-4">
        <input
          type="text"
          placeholder="ค้นหาพนักงาน..."
          value={searchTerm}
          onChange={handleSearch}
          className="border border-gray-300 rounded px-4 py-2"
        />
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-500 text-white rounded"
        >
          เพิ่มข้อมูล
        </button>
      </div>
      <table className="table-auto w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-4 py-2">#</th>
            <th className="border border-gray-300 px-4 py-2 cursor-pointer" onClick={() => handleSort("firstName")}>
                <div className="flex items-center justify-center">
                ชื่อ
                <svg
                  className="w-3 h-3 ms-1.5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z" />
                </svg>
                </div>
            </th>
            <th className="border border-gray-300 px-4 py-2 cursor-pointer" onClick={() => handleSort("lastName")}>
                <div className="flex items-center justify-center">
                นามสกุล
                <svg
                  className="w-3 h-3 ms-1.5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z" />
                </svg>
                </div>
            </th>
            <th className="border border-gray-300 px-4 py-2 cursor-pointer" onClick={() => handleSort("email")}>
                <div className="flex items-center justify-center">
                อีเมล
                <svg
                  className="w-3 h-3 ms-1.5"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z" />
                </svg>
                </div>
            </th>
            <th className="border border-gray-300 px-4 py-2">ดำเนินการ</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee, index) => (
            <tr key={employee.empId}>
              <td className="border border-gray-300 px-4 py-2 text-center">
                {(currentPage - 1) * itemsPerPage + index + 1}
              </td>
              <td className="border border-gray-300 px-4 py-2">
                {employee.firstName}
              </td>
              <td className="border border-gray-300 px-4 py-2">
                {employee.lastName}
              </td>
              <td className="border border-gray-300 px-4 py-2">
                {employee.email}
              </td>
              <td className="border border-gray-300 px-4 py-2 text-center">
                <button
                  onClick={() =>
                    navigate(`/panel/employee/view/${employee.empId}`)
                  }
                  className="text-blue-500 mr-2"
                >
                  แสดง
                </button>
                <button
                  onClick={() =>
                    navigate(`/panel/employee/edit/${employee.empId}`)
                  }
                  className="text-green-500 mr-2"
                >
                  แก้ไข
                </button>
                <button
                  onClick={() => handleDelete(employee.empId)}
                  className="text-red-500"
                >
                  ลบ
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-between items-center mt-4">
        <span>
          หน้า {currentPage} จาก {totalPages || 1}
        </span>
        {totalPages > 1 && (
          <div>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => handlePageChange(i + 1)}
                className={`px-3 py-1 mx-1 ${
                  currentPage === i + 1 ? "bg-blue-500 text-white" : "bg-gray-200"
                } rounded`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeList;